import { insertBefore } from '../html';
import { Watchers } from '../watcher';

const PLAYER_EVENTS = [
    'play', 'pause', 'stop',
    'mute', 'volume',
    'rate', 'duration', 'time',
];

export function AudioEmbed(player) {
    const element = <div class={`aembed aembed-${player.getType()}`}>{player}</div>;

    return {
        get element() { return element; },
        get player() { return player; },
        appendTo: function(target) {
            target.appendChild(element);
        },
        insertBefore: function(ref) {
            insertBefore(ref, element);
        },
        nuke: function() {
            element.remove();
        },
        replaceElement(target) {
            insertBefore(target, element);
            target.remove();
        },
    };
};

export function AudioEmbedPlayer(metadata, options) {
    options = options || {};

    const watchers = new Watchers;
    watchers.define(PLAYER_EVENTS);

    const shouldAutoplay = options.autoplay === undefined || options.autoplay;
    const haveNativeControls = options.nativeControls !== undefined && options.nativeControls;

    const element = <audio src={metadata.url} autoplay={shouldAutoplay} controls={haveNativeControls} />;

    const pub = {
        get element() { return element; },
        appendTo: function(target) {
            target.appendChild(element);
        },
        insertBefore: function(ref) {
            insertBefore(ref, element);
        },
        nuke: function() {
            element.remove();
        },
        replaceElement(target) {
            insertBefore(target, element);
            target.remove();
        },
        getType: function() { return 'external'; },
    };

    pub.watch = (name, handler) => watchers.watch(name, handler);
    pub.unwatch = (name, handler) => watchers.unwatch(name, handler);

    element.addEventListener('play', function() { watchers.call('play', pub); });

    const pPlay = function() { element.play(); };
    pub.play = pPlay;

    const pPause = function() { element.pause(); };
    pub.pause = pPause;

    let stopCalled = false;
    element.addEventListener('pause', function() {
        watchers.call(stopCalled ? 'stop' : 'pause', pub);
        stopCalled = false;
    });

    const pStop = function() {
        stopCalled = true;
        element.pause();
        element.currentTime = 0;
    };
    pub.stop = pStop;

    const pIsPlaying = function() { return !element.paused; };
    pub.isPlaying = pIsPlaying;

    const pIsMuted = function() { return element.muted; };
    pub.isMuted = pIsMuted;

    let lastMuteState = element.muted;
    element.addEventListener('volumechange', function() {
        if(lastMuteState !== element.muted) {
            lastMuteState = element.muted;
            watchers.call('mute', pub, [lastMuteState]);
        } else
            watchers.call('volume', pub, [element.volume]);
    });

    const pSetMuted = function(state) { element.muted = state; };
    pub.setMuted = pSetMuted;

    const pGetVolume = function() { return element.volume; };
    pub.getVolume = pGetVolume;

    const pSetVolume = function(volume) { element.volume = volume; };
    pub.setVolume = pSetVolume;

    const pGetPlaybackRate = function() { return element.playbackRate; };
    pub.getPlaybackRate = pGetPlaybackRate;

    element.addEventListener('ratechange', function() {
        watchers.call('rate', pub, [element.playbackRate]);
    });

    const pSetPlaybackRate = function(rate) { element.playbackRate = rate; };
    pub.setPlaybackRate = pSetPlaybackRate;

    window.addEventListener('durationchange', function() {
        watchers.call('duration', pub, [element.duration]);
    });

    const pGetDuration = function() { return element.duration; };
    pub.getDuration = pGetDuration;

    window.addEventListener('timeupdate', function() {
        watchers.call('time', pub, [element.currentTime]);
    });

    const pGetTime = function() { return element.currentTime; };
    pub.getTime = pGetTime;

    const pSeek = function(time) { element.currentTime = time; };
    pub.seek = pSeek;

    return pub;
};

export function AudioEmbedPlaceholder(metadata, options) {
    options = options || {};

    if(typeof options.player !== 'function' && typeof options.onclick !== 'function')
        throw 'Neither a player nor an onclick handler were provided.';

    let title = <h1 class="aembedph-info-title" />;
    let album = <p class="aembedph-info-album" />;
    if(metadata.media !== undefined && metadata.media.tags !== undefined) {
        const tags = metadata.media.tags;

        if(tags.title !== undefined) {
            if(tags.artist !== undefined) {
                title.appendChild(<span class="aembedph-info-title-artist">{tags.artist}</span>);
                title.appendChild(document.createTextNode(' - '));
            }

            title.appendChild(<span class="aembedph-info-title-title">{tags.title}</span>);
        } else {
            title.appendChild(<span class="aembedph-info-title-title">{metadata.title}</span>);
        }

        if(tags.album !== undefined && tags.album !== tags.title)
            album.textContent = tags.album;
    }

    const element = <div class={`aembedph aembedph-${(options.type || 'external')}`} style={{ '--aembedph-colour': metadata.color }} title={metadata.title}>
        <div class="aembedph-bg">
            <img src={metadata.image} alt="" onerror={function() { this.classList.add('aembedph-bg-none') }} />
        </div>
        <div class="aembedph-fg">
            <div class="aembedph-info">
                <div class="aembedph-info-cover">
                    <img src={metadata.image} alt="" onerror={function() { this.classList.add('aembedph-info-cover-none') }} />
                </div>
                <div class="aembedph-info-body">
                    {title}
                    {album}
                    <div class="aembedph-info-site">{metadata.site_name}</div>
                </div>
            </div>
            <div class="aembedph-play" onclick={function(ev) {
                if(ev.target.tagName.toLowerCase() === 'a')
                    return;

                if(typeof options.onclick === 'function') {
                    options.onclick(ev);
                    return;
                }

                const player = new options.player(metadata, options);

                const embed = new AudioEmbed(player);
                if(options.autoembed === undefined || options.autoembed)
                    embed.replaceElement(element);

                if(typeof options.onembed === 'function')
                    options.onembed(embed);
            }}>
                <div class="aembedph-play-internal">
                    <i class="fas fa-play fa-3x fa-fw" />
                </div>
                <div class="aembedph-play-external">
                    <a class="aembedph-play-external-link" href={metadata.url} target="_blank" rel="noopener">
                        {`or listen on ${metadata.site_name}?`}
                    </a>
                </div>
            </div>
        </div>
    </div>;

    const pub = {
        get element() { return element; },

        appendTo(target) {
            target.appendChild(element);
        },
        insertBefore(ref) {
            insertBefore(ref, element);
        },
        nuke() {
            element.remove();
        },
        replaceElement(target) {
            insertBefore(target, element);
            target.remove();
        }
    };

    return pub;
}
